import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TShareReactionType } from '../../services/share.service';

type AddMemorableMomentPayload = {
  qdosItemId: string;
  qdosEventId: string;
  qdosPostId: string;
  alternativeId?: string;
  mediaType?: string;
  imageId?: string;
  videoUrl?: string;
  description?: string;
  eventTitle?: string;
};

type RemoveShareItemPayload = {
  id: string;
  shareType?: 'team' | 'fan';
  eventId?: string;
};

type SetShareReactionPayload = {
  shareItemId: string;
  reactionType: TShareReactionType;
  shareType: 'team' | 'fan';
  eventId: string;
};

type RemoveShareReactionPayload = {
  shareItemId: string;
  shareType: 'team' | 'fan';
  eventId: string;
};

interface IShareState {
  isLoading: boolean;
  memorableMoments: any[];
  teamShare: any[];
  fanShare: any[];
  teamShareHistory: {
    currentEvent: any | null;
    previousEvents: any[];
  };
  fanShareHistory: {
    currentEvent: any | null;
    previousEvents: any[];
  };
}

const initialState: IShareState = {
  isLoading: false,
  memorableMoments: [],
  teamShare: [],
  fanShare: [],
  teamShareHistory: {
    currentEvent: null,
    previousEvents: [],
  },
  fanShareHistory: {
    currentEvent: null,
    previousEvents: [],
  },
};

const shareSlice = createSlice({
  name: 'share',
  initialState,
  reducers: {
    addToShare: (
      state,
      _action: PayloadAction<{
        qdosItemId: string;
        qdosEventId: string;
        shareType: 'team' | 'fan';
      }>,
    ) => {
      state.isLoading = true;
    },
    'addToShare/ok': (state) => {
      state.isLoading = false;
    },
    'addToShare/err': (state) => {
      state.isLoading = false;
    },

    addMemorableMoment: (
      state,
      _action: PayloadAction<AddMemorableMomentPayload>,
    ) => {
      state.isLoading = true;
    },
    'addMemorableMoment/ok': (state) => {
      state.isLoading = false;
    },
    'addMemorableMoment/err': (state) => {
      state.isLoading = false;
    },

    getMemorableMoments: (state) => {
      state.isLoading = true;
    },
    'getMemorableMoments/ok': (state, action: PayloadAction<any[] | undefined>) => {
      state.isLoading = false;
      state.memorableMoments = Array.isArray(action.payload) ? action.payload : [];
    },
    'getMemorableMoments/err': (state) => {
      state.isLoading = false;
      state.memorableMoments = [];
    },

    deleteMemorableMoment: (state, _action: PayloadAction<string>) => {
      state.isLoading = true;
    },
    'deleteMemorableMoment/ok': (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.memorableMoments = state.memorableMoments.filter(
        (item: any) => item._id !== action.payload,
      );
    },
    'deleteMemorableMoment/err': (state) => {
      state.isLoading = false;
    },

    getTeamShare: (state, _action: PayloadAction<string>) => {
      state.isLoading = true;
    },
    'getTeamShare/ok': (state, action: PayloadAction<any[] | undefined>) => {
      state.isLoading = false;
      state.teamShare = Array.isArray(action.payload) ? action.payload : [];
    },
    'getTeamShare/err': (state) => {
      state.isLoading = false;
      state.teamShare = [];
    },

    getFanShare: (state, _action: PayloadAction<string>) => {
      state.isLoading = true;
    },
    'getFanShare/ok': (state, action: PayloadAction<any[] | undefined>) => {
      state.isLoading = false;
      state.fanShare = Array.isArray(action.payload) ? action.payload : [];
    },
    'getFanShare/err': (state) => {
      state.isLoading = false;
      state.fanShare = [];
    },

    getTeamShareHistory: (state) => {
      state.isLoading = true;
    },
    'getTeamShareHistory/ok': (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.teamShareHistory = action.payload || {
        currentEvent: null,
        previousEvents: [],
      };
    },
    'getTeamShareHistory/err': (state) => {
      state.isLoading = false;
      state.teamShareHistory = {
        currentEvent: null,
        previousEvents: [],
      };
    },

    getFanShareHistory: (state) => {
      state.isLoading = true;
    },
    'getFanShareHistory/ok': (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.fanShareHistory = action.payload || {
        currentEvent: null,
        previousEvents: [],
      };
    },
    'getFanShareHistory/err': (state) => {
      state.isLoading = false;
      state.fanShareHistory = {
        currentEvent: null,
        previousEvents: [],
      };
    },

    setShareReaction: (
      state,
      _action: PayloadAction<SetShareReactionPayload>,
    ) => {
      state.isLoading = true;
    },
    'setShareReaction/ok': (state) => {
      state.isLoading = false;
    },
    'setShareReaction/err': (state) => {
      state.isLoading = false;
    },

    removeShareReaction: (
      state,
      _action: PayloadAction<RemoveShareReactionPayload>,
    ) => {
      state.isLoading = true;
    },
    'removeShareReaction/ok': (state) => {
      state.isLoading = false;
    },
    'removeShareReaction/err': (state) => {
      state.isLoading = false;
    },

    removeShareItem: (state, _action: PayloadAction<RemoveShareItemPayload>) => {
      state.isLoading = true;
    },
    'removeShareItem/ok': (state) => {
      state.isLoading = false;
    },
    'removeShareItem/err': (state) => {
      state.isLoading = false;
    },
  },
});

export const shareActions = shareSlice.actions;
export const shareReducer = shareSlice.reducer;